import type { Camera } from './camera';
import { cameraDepth, createCamera, faceFacing, project } from './camera';
import type { PickableState } from './picking';
import { heightAt } from './picking';

export type RenderableState = PickableState & {
  /** The ideal target height, rendered as a translucent horizontal sheet across the whole grid. Omit to hide the sheet. */
  optimalHeight?: number;
  heights: number[];
  hoverTile?: { x: number; y: number } | null;
  brushSize?: number;
  /** Height (in block units) at which to draw the translucent red fail plane, e.g. 0 for a floor breach or heightLimit for a ceiling breach. Null/omitted draws nothing. */
  failFlashHeight?: number | null;
  /** 0-1 intensity of the translucent red fail plane; 0 or omitted draws nothing even if failFlashHeight is set. */
  failFlashIntensity?: number;
  /** 0-1 intensity of a warning flash on BOTH boundary planes (floor + ceiling). Used for timer warnings. */
  warningFlashIntensity?: number;
  /** Set of tile indices (y*size+x) currently flashing due to height-match crossing. */
  flashTiles?: Set<number>;
  /** 0-1 intensity of the flash glow (fades out over the flash duration). */
  flashIntensity?: number;
};

type Point = { x: number; y: number };

const JUNGLE: [number, number, number] = [31, 77, 61];
const GOLD: [number, number, number] = [201, 162, 39];
const BRUSH_OVERLAY_SEGMENTS = 32;
/** Segment count for the small hover-tile footprint marker — smaller on screen than the brush ring, so fewer segments are plenty smooth. */
const FOOTPRINT_MARKER_SEGMENTS = 16;

/**
 * Compares two tiles for painter's-algorithm draw order (descending:
 * farthest-from-camera first, so nearer tiles are drawn later and
 * correctly occlude farther ones). Uses a two-tier comparison instead of a
 * single blended depth value:
 *
 * 1. Primary: depth contributed by the camera's dominant axis alone
 *    (whichever of world X/Y has the larger |camera.forward| component).
 *    Tiles sit at exact integer grid coordinates, so two tiles in
 *    different dominant-axis "rows" always have a robust,
 *    height-independent depth gap, for any camera rotation — the dominant
 *    axis is, by construction, never the near-degenerate one (at the
 *    worst case, exactly 45°, both axes contribute equally and robustly;
 *    the true degenerate case for one axis is exactly where the other
 *    axis becomes fully dominant instead).
 * 2. Secondary (only when two tiles share the same dominant-axis row): the
 *    tiles' full true camera depth (height included). Within a shared row
 *    the dominant-axis contribution is identical for both tiles, so
 *    ordering by full depth here is mathematically equivalent to ordering
 *    by just the remaining (secondary-axis + height) contribution —
 *    correctly making the taller tile "nearer" and drawn last, matching
 *    true 3D depth, without an arbitrary weighting constant.
 *
 * A single blended scalar (footprint depth plus a small height-weighted
 * tiebreak term) was tried and rejected: any weight large enough to fix
 * same-row cases near one axis's degenerate rotation was also large
 * enough to sometimes invert genuine cross-row order near the other
 * axis's degenerate rotation. Validated by a sweep across many grid sizes
 * and rotation angles — the blended-scalar approach failed near 90° for
 * every configuration tested, while this two-tier comparator holds
 * structurally, by construction, with no tuning.
 */
export function compareTileDrawOrder(
  camera: Camera,
  a: { x: number; y: number; height: number },
  b: { x: number; y: number; height: number },
): number {
  const dominantIsX = Math.abs(camera.forward.x) >= Math.abs(camera.forward.y);
  const rowA = dominantIsX ? a.x : a.y;
  const rowB = dominantIsX ? b.x : b.y;
  if (rowA !== rowB) {
    const camC = dominantIsX ? camera.position.x : camera.position.y;
    const f = dominantIsX ? camera.forward.x : camera.forward.y;
    return (rowB - camC) * f - (rowA - camC) * f; // descending: farther row first
  }
  return cameraDepth(camera, b.x, b.y, b.height) - cameraDepth(camera, a.x, a.y, a.height);
}

export function renderGrid(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  state: RenderableState,
): void {
  const { size, heightLimit, optimalHeight, heights, rotation } = state;
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  const camera = createCamera(
    canvasWidth, canvasHeight, size, heightLimit, rotation, state.bottomPadding,
    undefined, state.zoom, state.tilt,
  );

  const tiles: Array<{ x: number; y: number; height: number; idx: number }> = [];
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const height = heights[y * size + x];
      tiles.push({ x, y, height, idx: y * size + x });
    }
  }
  // Painter's algorithm: draw far-to-near so nearer blocks occlude farther
  // ones. Uses a two-tier comparator (see compareTileDrawOrder) rather than
  // a single blended depth value, so same-row occlusion (which needs
  // height to correctly decide) and cross-row occlusion (which must never
  // let height override true row position) are both handled correctly.
  tiles.sort((a, b) => compareTileDrawOrder(camera, a, b));

  const failFlashActive =
    !!state.failFlashIntensity && state.failFlashHeight !== null && state.failFlashHeight !== undefined;
  const failAlpha = failFlashActive ? Math.max(0, Math.min(1, state.failFlashIntensity as number)) : 0;
  const warningAlpha = state.warningFlashIntensity ? Math.max(0, Math.min(1, state.warningFlashIntensity)) : 0;

  // Per-tile sheet fragments to interleave with each tile's block so that
  // blocks taller than a sheet occlude it, while the sheet floats visibly
  // over blocks shorter than it (instead of the sheet always drawing on top
  // of every block, regardless of relative height).
  type SheetFragment = { height: number; fillStyle: string };
  const fragments: SheetFragment[] = [];
  if (optimalHeight != null) {
    fragments.push({ height: optimalHeight, fillStyle: 'rgba(136, 192, 208, 0.14)' });
  }
  if (failFlashActive) {
    fragments.push({ height: state.failFlashHeight as number, fillStyle: `rgba(214, 40, 40, ${failAlpha * 0.4})` });
  }
  if (warningAlpha > 0) {
    const warningColor = `rgba(255, 159, 67, ${warningAlpha * 0.35})`;
    fragments.push({ height: 0, fillStyle: warningColor });
    fragments.push({ height: heightLimit, fillStyle: warningColor });
  }

  // Layered rendering: for each sheet plane at height H, we draw:
  // 1. All block geometry below H (side faces clipped to [0, H])
  // 2. The whole-grid sheet at H (only on tiles shorter than H)
  // 3. All block geometry above H (side faces [H, top] + top face)
  //
  // This guarantees wall side-faces above the sheet are always drawn
  // ON TOP of the sheet, eliminating cross-tile z-order artifacts.

  // Sort fragments by height (ascending) so we can layer bottom-up
  const sortedFragments = [...fragments].sort((a, b) => a.height - b.height);

  if (sortedFragments.length === 0) {
    // No sheets — simple single-pass render
    for (const tile of tiles) {
      drawBlock(ctx, camera, tile.x, tile.y, tile.height, heightLimit, heights, size);
      if (state.flashTiles?.has(tile.idx) && state.flashIntensity) {
        strokeTileGlow(ctx, camera, tile.x, tile.y, tile.height, state.flashIntensity);
      }
    }
  } else {
    // Multi-pass layered render
    const cutHeights = sortedFragments.map((f) => f.height);

    // Pass 1: draw block geometry below the first sheet
    for (const tile of tiles) {
      drawBlock(ctx, camera, tile.x, tile.y, tile.height, heightLimit, heights, size, 0, cutHeights[0]);
    }

    // For each sheet: draw the sheet, then block geometry up to the next sheet
    for (let si = 0; si < sortedFragments.length; si += 1) {
      const fragment = sortedFragments[si];
      const nextCut = si < sortedFragments.length - 1 ? cutHeights[si + 1] : undefined;

      // Draw sheet on tiles shorter than this sheet height
      for (const tile of tiles) {
        if (tile.height < fragment.height) {
          fillTileQuad(ctx, camera, tile.x, tile.y, fragment.height, fragment.fillStyle);
        }
      }

      // Draw block geometry from this sheet to the next (or to top)
      for (const tile of tiles) {
        if (tile.height > fragment.height) {
          drawBlock(ctx, camera, tile.x, tile.y, tile.height, heightLimit, heights, size, fragment.height, nextCut);
        }
      }
    }

    // Flash glow on top of everything
    for (const tile of tiles) {
      if (state.flashTiles?.has(tile.idx) && state.flashIntensity) {
        strokeTileGlow(ctx, camera, tile.x, tile.y, tile.height, state.flashIntensity);
      }
    }
  }

  // Single clean outer-perimeter border strokes on top, deliberately not
  // occlusion-aware and not repeated per-tile, to avoid a faint interior
  // grid-line pattern across each sheet.
  if (optimalHeight != null) {
    strokeGridPerimeter(ctx, camera, size, optimalHeight, 'rgba(136, 192, 208, 0.4)');
  }
  strokeGridPerimeter(ctx, camera, size, 0, 'rgba(255, 159, 67, 0.3)');
  strokeGridPerimeter(ctx, camera, size, heightLimit, 'rgba(255, 159, 67, 0.3)');
  if (failFlashActive) {
    strokeGridPerimeter(ctx, camera, size, state.failFlashHeight as number, `rgba(214, 40, 40, ${failAlpha * 0.8})`);
  }
  if (warningAlpha > 0) {
    const warningStroke = `rgba(255, 159, 67, ${warningAlpha * 0.8})`;
    strokeGridPerimeter(ctx, camera, size, 0, warningStroke);
    strokeGridPerimeter(ctx, camera, size, heightLimit, warningStroke);
  }

  if (state.hoverTile) {
    const height = heightAt(heights, size, state.hoverTile.x, state.hoverTile.y);
    if (state.brushSize) {
      drawBrushRadiusOverlay(ctx, camera, state.hoverTile.x, state.hoverTile.y, height, state.brushSize);
    }
    drawTargetBeam(ctx, camera, state.hoverTile.x, state.hoverTile.y, height, heightLimit);
  }
}

/**
 * Computes the 4 screen-space corner points of a tile's top face by
 * projecting its world-space grid corners `(x±0.5, y±0.5, height)` straight
 * through the camera. Rotation is no longer applied here at all — the
 * camera's own orbit produces the rotated appearance — so this always uses
 * raw (unrotated) grid coordinates. Corners are returned in a consistent
 * grid-traversal order (top-left, top-right, bottom-right, bottom-left in
 * grid space), which projects to a consistent screen-space winding for the
 * face-normal shading below.
 */
function tileTopCorners(camera: Camera, x: number, y: number, height: number): [Point, Point, Point, Point] {
  return [
    project(camera, x - 0.5, y - 0.5, height),
    project(camera, x + 0.5, y - 0.5, height),
    project(camera, x + 0.5, y + 0.5, height),
    project(camera, x - 0.5, y + 0.5, height),
  ];
}

/**
 * Grid-space (dx, dy) offset to the neighboring tile for each of a tile's 4
 * side faces, in the same order as camera.ts's TILE_FACE_NORMALS (0: -Y
 * wall, 1: +X wall, 2: +Y wall, 3: -X wall).
 */
const FACE_NEIGHBOR_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
];

/** Grid coordinates of the tile adjacent to (x, y) across the given side face. */
export function neighborCoordsForFace(x: number, y: number, faceIndex: number): [number, number] {
  const [dx, dy] = FACE_NEIGHBOR_OFFSETS[faceIndex];
  return [x + dx, y + dy];
}

/**
 * The vertical extent of a side face that's actually visible given an
 * adjacent tile's height in that direction. Two touching solids never show
 * an interior seam: if the neighbor is at least as tall as this tile, the
 * whole face is hidden behind it (null). If the neighbor is shorter, only
 * the portion above the neighbor's height is a genuine visible step. Pass
 * neighborHeight=0 when there's no neighbor at all (grid edge) — nothing
 * occludes the wall there, so the full height range is visible.
 */
export function visibleFaceRange(height: number, neighborHeight: number): { from: number; to: number } | null {
  if (neighborHeight >= height) return null;
  return { from: neighborHeight, to: height };
}

/** Faces whose camera-facing dot product is below this threshold fade smoothly to 0 opacity instead of popping in/out. Expressed as a cosine-like value (dot of two unit vectors), not pixels. */
const FACE_FADE_THRESHOLD = 0.15;

/**
 * Draws a block's side faces and top face, with optional vertical
 * clipping. When clipFrom/clipTo are provided, only the portion of
 * each side face within that range is drawn, and the top face is
 * only drawn if clipTo >= height.
 */
function drawBlock(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  x: number,
  y: number,
  height: number,
  heightLimit: number,
  heights: number[],
  size: number,
  clipFrom?: number,
  clipTo?: number,
): void {
  const effectiveFrom = clipFrom ?? 0;
  const effectiveTo = clipTo ?? height;
  if (effectiveFrom >= effectiveTo || height <= 0) return;

  const drawTop = effectiveTo >= height;

  const baseColor = blockBaseColor(height, heightLimit);

  // Draw side faces with smooth interpolated shading.
  for (let i = 0; i < 4; i += 1) {
    const facing = faceFacing(camera, x, y, height, i);
    if (facing <= 0) continue;

    const [nx, ny] = neighborCoordsForFace(x, y, i);
    const neighborHeight = nx >= 0 && nx < size && ny >= 0 && ny < size ? heights[ny * size + nx] : 0;
    const range = visibleFaceRange(height, neighborHeight);
    if (range === null) continue;

    // Intersect visible range with clip range
    const faceFrom = Math.max(range.from, effectiveFrom);
    const faceTo = Math.min(range.to, effectiveTo);
    if (faceFrom >= faceTo) continue;

    const faceTopCorners = tileTopCorners(camera, x, y, faceTo);
    const faceBottomCorners = faceFrom === 0
      ? tileTopCorners(camera, x, y, 0)
      : tileTopCorners(camera, x, y, faceFrom);

    const a = faceTopCorners[i];
    const b = faceTopCorners[(i + 1) % 4];

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const faceNx = dy;
    const faceNy = -dx;
    const len = Math.sqrt(faceNx * faceNx + faceNy * faceNy);
    const nxNorm = len > 0 ? faceNx / len : 0;
    const factor = Math.max(0.45, Math.min(0.80, 0.635 - 0.19 * nxNorm));

    const fadeAlpha = Math.min(1, facing / FACE_FADE_THRESHOLD);

    const faceColor = shade(baseColor, factor);
    ctx.fillStyle = fadeAlpha < 1
      ? `rgba(${Math.round(faceColor[0])}, ${Math.round(faceColor[1])}, ${Math.round(faceColor[2])}, ${fadeAlpha})`
      : rgbToCss(faceColor);
    drawPolygon(ctx, [a, b, faceBottomCorners[(i + 1) % 4], faceBottomCorners[i]]);
  }

  // Top face — only when not clipped below the block's actual top
  if (drawTop) {
    const realTop = tileTopCorners(camera, x, y, height);
    ctx.fillStyle = rgbToCss(baseColor);
    drawPolygon(ctx, [realTop[0], realTop[1], realTop[2], realTop[3]]);
  }
}

/**
 * Fills a single tile's projected footprint at the given height with a
 * translucent color and no stroke. Interleaving these fragments into the
 * same per-tile draw order as blocks lets taller blocks correctly occlude
 * sheet fragments, while the sheet floats over shorter blocks.
 */
function fillTileQuad(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  x: number,
  y: number,
  height: number,
  fillStyle: string,
): void {
  const corners = tileTopCorners(camera, x, y, height);
  ctx.save();
  ctx.fillStyle = fillStyle;
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  ctx.lineTo(corners[1].x, corners[1].y);
  ctx.lineTo(corners[2].x, corners[2].y);
  ctx.lineTo(corners[3].x, corners[3].y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * Strokes a single clean outer-perimeter border around the whole slab at
 * the given height, with no fill. Used for the optimal-height sheet's
 * border, the fail-flash plane's border, and the two permanent floor/
 * ceiling boundary reference planes — deliberately a single whole-grid
 * stroke rather than per-tile, so it doesn't create an interior grid-line
 * pattern.
 */
function strokeGridPerimeter(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  size: number,
  height: number,
  strokeStyle: string,
): void {
  const half = 0.5;
  const corners: Array<[number, number]> = [
    [-half, -half],
    [size - half, -half],
    [size - half, size - half],
    [-half, size - half],
  ];

  const points = corners.map(([gx, gy]) => project(camera, gx, gy, height));

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let index = 1; index < points.length; index += 1) {
    ctx.lineTo(points[index].x, points[index].y);
  }
  ctx.closePath();
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
}

/**
 * Samples `segments` points evenly around a world-space circle (center,
 * radius) at a fixed height, and projects each through the camera. Used to
 * render perspective-correct circular indicators (brush radius ring,
 * hover-tile footprint marker) without assuming any particular
 * screen-space axis alignment — unlike drawing a `ctx.ellipse()` fitted
 * from just two projected edge-vector lengths, which can't correctly
 * represent a projected circle's true orientation (it continuously
 * rotates in screen space as the camera orbits).
 */
function projectWorldCircle(
  camera: Camera,
  centerX: number,
  centerY: number,
  height: number,
  radius: number,
  segments: number,
): Point[] {
  const points: Point[] = [];
  for (let i = 0; i <= segments; i += 1) {
    const angle = (i / segments) * Math.PI * 2;
    const worldX = centerX + Math.cos(angle) * radius;
    const worldY = centerY + Math.sin(angle) * radius;
    points.push(project(camera, worldX, worldY, height));
  }
  return points;
}

/**
 * Draws a translucent outline showing the brush's current radius of effect
 * around the hovered tile, so the player can see exactly which tiles a
 * stroke will reach before committing to it. Sampling a world-space circle
 * and projecting each sampled point through the camera (projectWorldCircle)
 * turns it into a perspective-correct ellipse/distorted shape for free.
 */
function drawBrushRadiusOverlay(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  centerX: number,
  centerY: number,
  height: number,
  radius: number,
): void {
  const points = projectWorldCircle(camera, centerX, centerY, height, radius, BRUSH_OVERLAY_SEGMENTS);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let index = 1; index < points.length; index += 1) {
    ctx.lineTo(points[index].x, points[index].y);
  }
  ctx.closePath();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.restore();
}

/**
 * Draws a vertical beam over the tile currently targeted by the pointer, so
 * it remains visible even when occluded behind taller neighboring blocks.
 */
function drawTargetBeam(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  centerX: number,
  centerY: number,
  height: number,
  heightLimit: number,
): void {
  const anchor = project(camera, centerX, centerY, height);
  const beamTop = project(camera, centerX, centerY, heightLimit + 4);

  ctx.save();
  const gradient = ctx.createLinearGradient(beamTop.x, beamTop.y, anchor.x, anchor.y);
  gradient.addColorStop(0, 'rgba(255, 214, 102, 0)');
  gradient.addColorStop(1, 'rgba(255, 214, 102, 0.85)');
  ctx.strokeStyle = gradient;
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(beamTop.x, beamTop.y);
  ctx.lineTo(anchor.x, anchor.y);
  ctx.stroke();
  ctx.setLineDash([]);

  // Footprint marker: sample the tile's footprint circle (radius 0.5,
  // matching a tile's half-width) and project each point, instead of
  // fitting a ctx.ellipse() from just two edge-vector lengths — that
  // shortcut assumed the projected +X/+Y edges were screen-axis-aligned,
  // which only holds at specific camera angles and otherwise produces a
  // wrongly-oriented ellipse as the camera orbits.
  const footprintPoints = projectWorldCircle(camera, centerX, centerY, height, 0.5, FOOTPRINT_MARKER_SEGMENTS);
  ctx.beginPath();
  ctx.moveTo(footprintPoints[0].x, footprintPoints[0].y);
  for (let index = 1; index < footprintPoints.length; index += 1) {
    ctx.lineTo(footprintPoints[index].x, footprintPoints[index].y);
  }
  ctx.closePath();
  ctx.strokeStyle = 'rgba(255, 214, 102, 0.9)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

function drawPolygon(ctx: CanvasRenderingContext2D, points: Point[]): void {
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let index = 1; index < points.length; index += 1) {
    ctx.lineTo(points[index].x, points[index].y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.25)';
  ctx.lineWidth = 1;
  ctx.stroke();
}

function blockBaseColor(height: number, heightLimit: number): [number, number, number] {
  const t = heightLimit === 0 ? 0 : height / heightLimit;
  return mixColor(JUNGLE, GOLD, t);
}

function mixColor(
  from: [number, number, number],
  to: [number, number, number],
  t: number,
): [number, number, number] {
  return [
    from[0] + (to[0] - from[0]) * t,
    from[1] + (to[1] - from[1]) * t,
    from[2] + (to[2] - from[2]) * t,
  ];
}

function shade(color: [number, number, number], factor: number): [number, number, number] {
  return [color[0] * factor, color[1] * factor, color[2] * factor];
}

function rgbToCss([r, g, b]: [number, number, number]): string {
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

/**
 * Draws a subtle light-green glow outline on a tile's top face to indicate
 * that its height just crossed through equality with a neighbor.
 */
function strokeTileGlow(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  x: number,
  y: number,
  height: number,
  intensity: number,
): void {
  const corners = tileTopCorners(camera, x, y, height);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  ctx.lineTo(corners[1].x, corners[1].y);
  ctx.lineTo(corners[2].x, corners[2].y);
  ctx.lineTo(corners[3].x, corners[3].y);
  ctx.closePath();
  ctx.strokeStyle = `rgba(144, 238, 144, ${0.7 * intensity})`;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = `rgba(144, 238, 144, ${0.15 * intensity})`;
  ctx.fill();
  ctx.restore();
}
